import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import MainLayout from '@/components/layout/MainLayout';
import { useKnowledgeBases } from '@/hooks/useKnowledgeBases';
import { useKnowledgeBaseCategories } from '@/hooks/useKnowledgeBaseCategories';
import type { KnowledgeBase as KnowledgeBaseType, KnowledgeBaseDocument } from '@/components/knowledge/types';
import KnowledgeBaseContainer from '@/components/knowledge/KnowledgeBaseContainer';
import KnowledgeBaseDialogs from '@/components/knowledge/KnowledgeBaseDialogs';
import LibrarianExportDialog from '@/components/knowledge/LibrarianExportDialog';
import LibrarianIngestDialog from '@/components/knowledge/LibrarianIngestDialog';
import LibrarianLintDialog from '@/components/knowledge/LibrarianLintDialog';
import { useKnowledgeBaseVault } from '@/hooks/useKnowledgeBaseVault';
import { useKnowledgeBaseVaultPages } from '@/hooks/useKnowledgeBaseVaultPages';
import { useLibrarianOperations } from '@/hooks/useLibrarianOperations';
import { cn } from '@/lib/utils';

interface ActiveKnowledgeBase extends Omit<KnowledgeBaseType, 'documents'> {
  documents?: KnowledgeBaseDocument[];
}

interface KnowledgeBasePageProps {
  embeddedInMapleOS?: boolean;
  initialKnowledgeBaseId?: string;
  appTitle?: string;
}

const KnowledgeBasePage = ({
  embeddedInMapleOS = false,
  initialKnowledgeBaseId,
  appTitle,
}: KnowledgeBasePageProps) => {
  const location = useLocation();
  const {
    knowledgeBases,
    loading,
    fetchKnowledgeBases,
    getKnowledgeBase,
    getKnowledgeBaseShares,
    createKnowledgeBase,
    deleteKnowledgeBase,
    sharesLoading,
  } = useKnowledgeBases();
  const {
    categories,
    assignmentByKnowledgeBaseId,
    loading: categoriesLoading,
    fetchKnowledgeBaseCategories,
    createCategory,
    moveKnowledgeBasesToCategory,
  } = useKnowledgeBaseCategories();

  const [activeKnowledgeBase, setActiveKnowledgeBase] = useState<ActiveKnowledgeBase | null>(null);
  const [activeDocument, setActiveDocument] = useState<KnowledgeBaseDocument | null>(null);
  const [shares, setShares] = useState<any[]>([]);
  const [openKnowledgeBaseIds, setOpenKnowledgeBaseIds] = useState<string[]>([]);
  const [activeLibraryTabId, setActiveLibraryTabId] = useState<string | null>(initialKnowledgeBaseId ?? null);
  const [activeWorkspaceTab, setActiveWorkspaceTab] = useState<'sources' | 'vault' | 'graph' | 'activity'>('sources');
  const [editingKnowledgeBase, setEditingKnowledgeBase] = useState<KnowledgeBaseType | null>(null);
  const [pendingDeleteKnowledgeBases, setPendingDeleteKnowledgeBases] = useState<KnowledgeBaseType[]>([]);
  const [createTargetCategoryId, setCreateTargetCategoryId] = useState<string | null>(null);
  const [createCategoryLabel, setCreateCategoryLabel] = useState<string | null>(null);
  const [uploadAfterCreate, setUploadAfterCreate] = useState(true);

  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [documentOpen, setDocumentOpen] = useState(false);
  const [editDocumentOpen, setEditDocumentOpen] = useState(false);
  const [deleteDocumentOpen, setDeleteDocumentOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareAccessLevel, setShareAccessLevel] = useState<'View' | 'Edit' | 'Manage'>('View');
  const [ingestDialogOpen, setIngestDialogOpen] = useState(false);
  const [lintDialogOpen, setLintDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const fullscreenHostRef = useRef<HTMLDivElement>(null);

  const isEmbedded = useMemo(
    () => embeddedInMapleOS || new URLSearchParams(location.search).get('embeddedInMapleOS') === 'true',
    [embeddedInMapleOS, location.search]
  );
  const resolvedAppTitle = appTitle ?? (isEmbedded ? 'AI Knowledge' : 'Knowledge Base');
  const activeKnowledgeBaseId = activeLibraryTabId;

  const knowledgeBasesWithCategories = useMemo(
    () => knowledgeBases.map((knowledgeBase) => ({
      ...knowledgeBase,
      category_id: assignmentByKnowledgeBaseId[knowledgeBase.id] ?? null,
      category: categories.find((category) => category.id === assignmentByKnowledgeBaseId[knowledgeBase.id]) ?? null,
    })),
    [assignmentByKnowledgeBaseId, categories, knowledgeBases]
  );

  const activeKnowledgeBaseSummary = useMemo(
    () => knowledgeBasesWithCategories.find((knowledgeBase) => knowledgeBase.id === activeKnowledgeBaseId) ?? null,
    [activeKnowledgeBaseId, knowledgeBasesWithCategories]
  );

  const validKnowledgeBaseIds = useMemo(
    () => new Set(knowledgeBasesWithCategories.map((knowledgeBase) => knowledgeBase.id)),
    [knowledgeBasesWithCategories]
  );

  useEffect(() => {
    void fetchKnowledgeBases();
    void fetchKnowledgeBaseCategories();
  }, [fetchKnowledgeBaseCategories, fetchKnowledgeBases]);

  useEffect(() => {
    if (!initialKnowledgeBaseId) return;
    if (!validKnowledgeBaseIds.has(initialKnowledgeBaseId)) return;

    setOpenKnowledgeBaseIds((current) => (
      current.includes(initialKnowledgeBaseId) ? current : [...current, initialKnowledgeBaseId]
    ));
    setActiveLibraryTabId((current) => current ?? initialKnowledgeBaseId);
  }, [initialKnowledgeBaseId, validKnowledgeBaseIds]);

  useEffect(() => {
    setOpenKnowledgeBaseIds((current) => {
      const next = current.filter((knowledgeBaseId) => validKnowledgeBaseIds.has(knowledgeBaseId));
      return next.length === current.length ? current : next;
    });

    setActiveLibraryTabId((current) => {
      if (current && validKnowledgeBaseIds.has(current)) {
        return current;
      }
      return null;
    });
  }, [validKnowledgeBaseIds]);

  useEffect(() => {
    setActiveWorkspaceTab('sources');
  }, [activeKnowledgeBaseId]);

  useEffect(() => {
    if (activeKnowledgeBaseId) {
      setOpenKnowledgeBaseIds((current) => (
        current.includes(activeKnowledgeBaseId) ? current : [...current, activeKnowledgeBaseId]
      ));
    }
  }, [activeKnowledgeBaseId]);

  useEffect(() => {
    if (!activeKnowledgeBaseSummary) {
      setActiveKnowledgeBase(null);
      setShares([]);
      return;
    }

    let cancelled = false;

    const loadKnowledgeBaseDetails = async () => {
      const [knowledgeBaseDetails, sharesData] = await Promise.all([
        getKnowledgeBase(activeKnowledgeBaseSummary.id),
        getKnowledgeBaseShares(activeKnowledgeBaseSummary.id),
      ]);

      if (cancelled) {
        return;
      }

      if (knowledgeBaseDetails) {
        setActiveKnowledgeBase({
          ...knowledgeBaseDetails,
          category_id: activeKnowledgeBaseSummary.category_id ?? null,
          category: activeKnowledgeBaseSummary.category ?? null,
        });
      }

      setShares(sharesData);
    };

    void loadKnowledgeBaseDetails();

    return () => {
      cancelled = true;
    };
  }, [activeKnowledgeBaseSummary, getKnowledgeBase, getKnowledgeBaseShares]);

  const { vault, loading: vaultLoading, ensureVault, refreshVault } = useKnowledgeBaseVault(activeKnowledgeBaseId);
  const {
    pages: vaultPages,
    links: vaultLinks,
    runs: vaultRuns,
    loading: vaultContentLoading,
    refresh: refreshVaultPages,
  } = useKnowledgeBaseVaultPages(vault?.id);

  const refreshLibrarian = useCallback(async () => {
    await refreshVault();
    await refreshVaultPages();

    if (!activeKnowledgeBaseId) {
      return;
    }

    const [knowledgeBaseDetails, sharesData] = await Promise.all([
      getKnowledgeBase(activeKnowledgeBaseId),
      getKnowledgeBaseShares(activeKnowledgeBaseId),
    ]);

    if (knowledgeBaseDetails) {
      const currentSummary = knowledgeBasesWithCategories.find((knowledgeBase) => knowledgeBase.id === activeKnowledgeBaseId);
      setActiveKnowledgeBase({
        ...knowledgeBaseDetails,
        category_id: currentSummary?.category_id ?? null,
        category: currentSummary?.category ?? null,
      });
    }

    setShares(sharesData);
  }, [activeKnowledgeBaseId, getKnowledgeBase, getKnowledgeBaseShares, knowledgeBasesWithCategories, refreshVault, refreshVaultPages]);

  const librarian = useLibrarianOperations(activeKnowledgeBaseId, refreshLibrarian);

  const handleRequestCreateKnowledgeBase = useCallback((categoryId: string | null = null, categoryLabel?: string | null) => {
    setCreateTargetCategoryId(categoryId);
    setCreateCategoryLabel(categoryLabel ?? null);
    setUploadAfterCreate(true);
    setOpen(true);
  }, []);

  const handleCreateKnowledgeBase = useCallback(async (values: { name: string; description?: string; isPrivate?: boolean }) => {
    const newKnowledgeBaseId = await createKnowledgeBase(
      values.name,
      values.description || '',
      undefined,
      values.isPrivate || false
    );

    if (!newKnowledgeBaseId) {
      return;
    }

    if (createTargetCategoryId) {
      await moveKnowledgeBasesToCategory([newKnowledgeBaseId], createTargetCategoryId);
    }

    await fetchKnowledgeBases();

    const targetCategory = createTargetCategoryId
      ? categories.find((category) => category.id === createTargetCategoryId) ?? null
      : null;

    const knowledgeBaseDetails = await getKnowledgeBase(newKnowledgeBaseId);
    if (knowledgeBaseDetails) {
      setActiveKnowledgeBase({
        ...knowledgeBaseDetails,
        category_id: targetCategory?.id ?? null,
        category: targetCategory,
      });
    }

    setOpenKnowledgeBaseIds((current) => (
      current.includes(newKnowledgeBaseId) ? current : [...current, newKnowledgeBaseId]
    ));
    setActiveLibraryTabId(newKnowledgeBaseId);
    setOpen(false);
  }, [categories, createKnowledgeBase, createTargetCategoryId, fetchKnowledgeBases, getKnowledgeBase, moveKnowledgeBasesToCategory]);

  const handleCloseKnowledgeBase = useCallback((knowledgeBaseId: string) => {
    setOpenKnowledgeBaseIds((current) => current.filter((id) => id !== knowledgeBaseId));

    setActiveLibraryTabId((current) => {
      if (current !== knowledgeBaseId) {
        return current;
      }

      const remaining = openKnowledgeBaseIds.filter((id) => id !== knowledgeBaseId);
      return remaining[remaining.length - 1] ?? null;
    });

    if (activeKnowledgeBase?.id === knowledgeBaseId) {
      setActiveKnowledgeBase(null);
      setShares([]);
    }
  }, [activeKnowledgeBase?.id, openKnowledgeBaseIds]);

  const handleEditDocument = (doc: KnowledgeBaseDocument) => {
    setActiveDocument(doc);
    setEditDocumentOpen(true);
  };

  const handleDeleteDocumentClick = (doc: KnowledgeBaseDocument) => {
    setActiveDocument(doc);
    setDeleteDocumentOpen(true);
  };

  const handleInitializeVault = async () => {
    const initializedVault = await librarian.initialize();
    if (initializedVault) {
      setActiveWorkspaceTab('vault');
    }
    setLintDialogOpen(false);
  };

  const handleProcessDocument = async (document: KnowledgeBaseDocument) => {
    if (!vault) {
      await handleInitializeVault();
    }

    const result = await librarian.ingestDocument(document);
    if (result) {
      setActiveWorkspaceTab('vault');
    }
    return result;
  };

  const handleCreateCategory = useCallback(async (name: string) => {
    await createCategory(name);
  }, [createCategory]);

  const handleMoveKnowledgeBasesToCategory = useCallback(async (knowledgeBaseIds: string[], categoryId: string | null) => {
    const moved = await moveKnowledgeBasesToCategory(knowledgeBaseIds, categoryId);

    if (!moved) {
      return false;
    }

    if (activeKnowledgeBase && knowledgeBaseIds.includes(activeKnowledgeBase.id)) {
      const nextCategory = categoryId
        ? categories.find((category) => category.id === categoryId) ?? null
        : null;

      setActiveKnowledgeBase((current) => (
        current
          ? {
              ...current,
              category_id: categoryId,
              category: nextCategory,
            }
          : current
      ));
    }

    return true;
  }, [activeKnowledgeBase, categories, moveKnowledgeBasesToCategory]);

  const handleRequestEditKnowledgeBase = useCallback((knowledgeBase: KnowledgeBaseType) => {
    setEditingKnowledgeBase(knowledgeBase);
    setEditOpen(true);
  }, []);

  const openDeleteDialogForKnowledgeBases = useCallback((items: KnowledgeBaseType[]) => {
    if (items.length === 0) return;
    setPendingDeleteKnowledgeBases(items);
    setDeleteOpen(true);
  }, []);

  const handleRequestDeleteKnowledgeBase = useCallback((knowledgeBase: KnowledgeBaseType) => {
    openDeleteDialogForKnowledgeBases([knowledgeBase]);
  }, [openDeleteDialogForKnowledgeBases]);

  const handleRequestDeleteSelectedKnowledgeBases = useCallback((ids: string[]) => {
    const selectedKnowledgeBases = knowledgeBasesWithCategories.filter((knowledgeBase) => ids.includes(knowledgeBase.id));
    openDeleteDialogForKnowledgeBases(selectedKnowledgeBases);
  }, [knowledgeBasesWithCategories, openDeleteDialogForKnowledgeBases]);

  const handleDeleteKnowledgeBases = useCallback(async () => {
    if (pendingDeleteKnowledgeBases.length === 0) {
      return false;
    }

    let allDeleted = true;
    for (const knowledgeBase of pendingDeleteKnowledgeBases) {
      const deleted = await deleteKnowledgeBase(knowledgeBase.id);
      if (!deleted) {
        allDeleted = false;
        break;
      }
    }

    if (!allDeleted) {
      return false;
    }

    const deletedIds = new Set(pendingDeleteKnowledgeBases.map((knowledgeBase) => knowledgeBase.id));
    setOpenKnowledgeBaseIds((current) => current.filter((knowledgeBaseId) => !deletedIds.has(knowledgeBaseId)));
    setActiveLibraryTabId((current) => {
      if (!current || !deletedIds.has(current)) {
        return current;
      }
      const remaining = openKnowledgeBaseIds.filter((knowledgeBaseId) => !deletedIds.has(knowledgeBaseId));
      return remaining[remaining.length - 1] ?? null;
    });

    if (activeKnowledgeBase && deletedIds.has(activeKnowledgeBase.id)) {
      setActiveKnowledgeBase(null);
      setShares([]);
    }

    setPendingDeleteKnowledgeBases([]);
    setDeleteOpen(false);
    await fetchKnowledgeBases();
    return true;
  }, [activeKnowledgeBase, deleteKnowledgeBase, fetchKnowledgeBases, openKnowledgeBaseIds, pendingDeleteKnowledgeBases]);

  return (
    <div className={cn('h-full min-h-0', isEmbedded ? 'bg-transparent' : 'bg-background')}>
      <div ref={fullscreenHostRef} className="h-full min-h-0">
        <div className={cn('h-full min-h-0', isEmbedded && 'p-4 md:p-5')}>
          <KnowledgeBaseContainer
            appTitle={resolvedAppTitle}
            knowledgeBases={knowledgeBasesWithCategories}
            categories={categories}
            assignmentByKnowledgeBaseId={assignmentByKnowledgeBaseId}
            openKnowledgeBaseIds={openKnowledgeBaseIds}
            activeKnowledgeBaseId={activeKnowledgeBaseId}
            loading={loading}
            categoriesLoading={categoriesLoading}
            activeKnowledgeBase={activeKnowledgeBase}

            onOpenKnowledgeBase={(knowledgeBase) => {
              setOpenKnowledgeBaseIds((current) => (
                current.includes(knowledgeBase.id) ? current : [...current, knowledgeBase.id]
              ));
              setActiveLibraryTabId(knowledgeBase.id);
            }}
            onActivateKnowledgeBaseTab={setActiveLibraryTabId}
            onCloseKnowledgeBase={handleCloseKnowledgeBase}
            onCreateKnowledgeBase={() => handleRequestCreateKnowledgeBase(null, null)}
            onCreateKnowledgeBaseInCategory={handleRequestCreateKnowledgeBase}
            onEditKnowledgeBase={handleRequestEditKnowledgeBase}
            onRequestDeleteKnowledgeBase={handleRequestDeleteKnowledgeBase}
            onRequestDeleteSelectedKnowledgeBases={handleRequestDeleteSelectedKnowledgeBases}
            onCreateCategory={handleCreateCategory}
            onMoveKnowledgeBasesToCategory={handleMoveKnowledgeBasesToCategory}
            onAddDocument={() => setDocumentOpen(true)}
            onShare={() => setShareDialogOpen(true)}
            onEditDocument={handleEditDocument}
            onDeleteDocument={handleDeleteDocumentClick}
            onProcessDocument={(document) => {
              void handleProcessDocument(document);
            }}
            onOpenIngestDialog={() => setIngestDialogOpen(true)}
            onOpenLintDialog={() => setLintDialogOpen(true)}
            onOpenExportDialog={() => setExportDialogOpen(true)}
            onInitializeVault={() => {
              void handleInitializeVault();
            }}
            vaultPages={vaultPages}
            vaultLinks={vaultLinks}
            vaultRuns={vaultRuns}
            vaultLoading={vaultLoading || vaultContentLoading}
            hasVault={Boolean(vault)}
            activeWorkspaceTab={activeWorkspaceTab}
            onActiveWorkspaceTabChange={setActiveWorkspaceTab}
            graphRefreshKey={`${activeKnowledgeBaseId ?? 'none'}:${vaultPages.length}:${vaultLinks.length}:${vaultRuns.length}`}
            fullscreenHostRef={fullscreenHostRef}
          />
        </div>
      </div>

      <KnowledgeBaseDialogs
        open={open}
        setOpen={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) {
            setCreateTargetCategoryId(null);
            setCreateCategoryLabel(null);
            setUploadAfterCreate(true);
          }
        }}
        editOpen={editOpen}
        setEditOpen={(nextOpen) => {
          setEditOpen(nextOpen);
          if (!nextOpen) {
            setEditingKnowledgeBase(null);
          }
        }}
        deleteOpen={deleteOpen}
        setDeleteOpen={(nextOpen) => {
          setDeleteOpen(nextOpen);
          if (!nextOpen) {
            setPendingDeleteKnowledgeBases([]);
          }
        }}
        deleteCount={pendingDeleteKnowledgeBases.length || 1}
        deleteLabel={pendingDeleteKnowledgeBases.length === 1 ? pendingDeleteKnowledgeBases[0]?.name : undefined}
        onDeleteKnowledgeBases={pendingDeleteKnowledgeBases.length > 0 ? handleDeleteKnowledgeBases : undefined}
        documentOpen={documentOpen}
        setDocumentOpen={setDocumentOpen}
        editDocumentOpen={editDocumentOpen}
        setEditDocumentOpen={(nextOpen) => {
          setEditDocumentOpen(nextOpen);
          if (!nextOpen) {
            setActiveDocument(null);
          }
        }}
        deleteDocumentOpen={deleteDocumentOpen}
        setDeleteDocumentOpen={(nextOpen) => {
          setDeleteDocumentOpen(nextOpen);
          if (!nextOpen) {
            setActiveDocument(null);
          }
        }}
        shareDialogOpen={shareDialogOpen}
        setShareDialogOpen={setShareDialogOpen}
        activeKnowledgeBase={editingKnowledgeBase ?? activeKnowledgeBase}
        activeDocument={activeDocument}
        shares={shares}
        sharesLoading={sharesLoading}
        shareEmail={shareEmail}
        setShareEmail={setShareEmail}
        shareAccessLevel={shareAccessLevel}
        setShareAccessLevel={setShareAccessLevel}
        fetchKnowledgeBaseDetails={refreshLibrarian}
        fetchKnowledgeBases={fetchKnowledgeBases}
        onCreateKnowledgeBase={handleCreateKnowledgeBase}
        createCategoryLabel={createCategoryLabel}
        uploadAfterCreate={uploadAfterCreate}
        setUploadAfterCreate={setUploadAfterCreate}
        onProcessWithLibrarian={handleProcessDocument}
      />

      <LibrarianIngestDialog
        open={ingestDialogOpen}
        onOpenChange={setIngestDialogOpen}
        documents={activeKnowledgeBase?.documents ?? []}
        onIngest={handleProcessDocument}
      />

      <LibrarianLintDialog
        open={lintDialogOpen}
        onOpenChange={setLintDialogOpen}
        result={librarian.lastLintResult}
        onRunLint={async () => {
          await librarian.runLint();
        }}
      />

      <LibrarianExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        onExport={async () => {
          await librarian.exportVault();
        }}
        pageCount={vaultPages.length}
      />
    </div>
  );
};

const KnowledgeBasePageLayout = (props: KnowledgeBasePageProps) => {
  if (props.embeddedInMapleOS) {
    return <KnowledgeBasePage {...props} />;
  }

  return (
    <MainLayout>
      <KnowledgeBasePage {...props} />
    </MainLayout>
  );
};

export default KnowledgeBasePageLayout;